import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  User,
  LayoutDashboard,
  LogOut,
  ChevronDown
} from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [weatherData, setWeatherData] = useState(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(true);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Weather Data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setIsLoadingWeather(true);
        // Using Colombo, LK as default location for TravelAura
        const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?q=Colombo,lk&units=metric&appid=${API_KEY}`
        );
        const data = await response.json();

        if (response.ok) {
          setWeatherData({
            temp: Math.round(data.main.temp),
            icon: `https://openweathermap.org/img/wn/${data.weather[0].icon}.png`,
            description: data.weather[0].description
          });
        }
      } catch (error) {
        console.error("Failed to fetch weather data:", error);
      } finally {
        setIsLoadingWeather(false);
      }
    };

    fetchWeather();
    // Refresh weather every 30 minutes
    const intervalId = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

        {/* Brand Logo */}
        <div className="flex items-center">
          <Link
            to="/"
            className="text-2xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-teal-500 hover:opacity-80 transition-opacity pr-6 border-r border-gray-100"
          >
            TravelAura
          </Link>

          {/* Weather Widget */}
          <div className="hidden lg:flex items-center gap-2 pl-6">
            {isLoadingWeather ? (
              <div className="w-16 h-6 animate-pulse bg-gray-100 rounded-md"></div>
            ) : weatherData ? (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 rounded-full border border-sky-100" title={weatherData.description}>
                <img src={weatherData.icon} alt="Weather" className="w-6 h-6" />
                <span className="text-xs font-black text-sky-900">{weatherData.temp}°C</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Nav Links */}
        <div className="hidden md:flex items-center gap-8 text-sm font-bold text-gray-600">
          <Link to="/" className="hover:text-sky-600 transition-colors">Home</Link>
          <Link to="/destinationsuser" className="hover:text-sky-600 transition-colors">Destinations</Link>
          <Link to="/events" className="hover:text-sky-600 transition-colors">Events</Link>
          <Link to="/viewblog" className="hover:text-sky-600 transition-colors">Blogs</Link>
          <Link to="/useraccommodations" className="hover:text-sky-600 transition-colors">Stay</Link>
          <Link to="/usertravelpackages" className="hover:text-sky-600 transition-colors">Packages</Link>
          <Link to="/viewreview" className="hover:text-sky-600 transition-colors">Reviews</Link>
        </div>

        {/* Authentication / Profile */}
        <div className="flex items-center gap-4">
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-3 pl-4 pr-3 py-2 bg-sky-50 rounded-full border border-sky-100 hover:border-sky-300 transition-all group"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-teal-400 flex items-center justify-center text-white text-xs font-black shadow-lg shadow-sky-500/20 group-hover:scale-105 transition-transform">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-black text-sky-950 hidden sm:block">
                  {user.name.split(" ")[0]}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-sky-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-white rounded-3xl shadow-2xl shadow-sky-900/10 border border-sky-50 py-3 overflow-hidden animate-in fade-in zoom-in duration-200 origin-top-right">
                  <div className="px-5 py-4 border-b border-gray-50 mb-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Signed in as</p>
                    <p className="text-sm font-black text-sky-950 truncate">{user.email}</p>
                  </div>

                  <Link
                    to={user.role === "admin" ? "/admindash" : "/user-dashboard"}
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm font-bold text-gray-600 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                  >
                    <LayoutDashboard size={18} className="text-sky-500" />
                    Dashboard
                  </Link>

                  <Link
                    to="/user-dashboard"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm font-bold text-gray-600 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                  >
                    <User size={18} className="text-sky-500" />
                    My Profile
                  </Link>

                  <div className="h-px bg-gray-50 my-2 mx-5"></div>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-red-500 hover:bg-red-50 transition-colors text-left"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm font-black text-gray-600 hover:text-sky-600 transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-6 py-2.5 bg-sky-600 text-white text-sm font-black rounded-full hover:bg-sky-700 shadow-xl shadow-sky-600/20 active:scale-95 transition-all"
              >
                Register
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
